import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type EmploymentAnalysis } from '../../employmentService'
import {
  EmploymentTrendChart,
  type EmploymentTrendChartRef,
} from '../charts/EmploymentTrendChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartGrid,
  ChartContainer,
} from '../../EmploymentTab.styles'

export interface EmploymentGrowthTabProps {
  data: EmploymentAnalysis | null
  t: TFunction
}

export const EmploymentGrowthTab: React.FC<EmploymentGrowthTabProps> = ({
  data,
  t,
}) => {
  const totalEmploymentChartRef = useRef<EmploymentTrendChartRef>(null)
  const fullTimeVsTotalChartRef = useRef<EmploymentTrendChartRef>(null)

  if (!data) return null

  const { current } = data

  const getGrowthDisplayValue = (evolution: number): string => {
    const percentage = Math.abs(evolution * 100)
    const sign = evolution >= 0 ? '+' : '-'
    return `${sign}${percentage.toFixed(1)}%`
  }

  const getGrowthVariant = (
    evolution: number
  ): 'success' | 'warning' | 'danger' | 'default' => {
    if (evolution > 0.05) return 'success'
    if (evolution > 0) return 'default'
    if (evolution > -0.05) return 'warning'
    return 'danger'
  }

  return (
    <TabContentContainer>
      {/* Growth Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.oneYearGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_employment_evolution_1_year || 0
          )}
          subtitle={t('metrics.oneYearGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_1_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.oneYearGrowth.title')}
              shortDescription={t('tooltips.oneYearGrowth.shortDescription')}
              calculation={t('tooltips.oneYearGrowth.calculation')}
              importance={t('tooltips.oneYearGrowth.importance')}
              limitations={t('tooltips.oneYearGrowth.limitations')}
              source={t('tooltips.oneYearGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.twoYearGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_employment_evolution_2_year || 0
          )}
          subtitle={t('metrics.twoYearGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_2_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.twoYearGrowth.title')}
              shortDescription={t('tooltips.twoYearGrowth.shortDescription')}
              calculation={t('tooltips.twoYearGrowth.calculation')}
              importance={t('tooltips.twoYearGrowth.importance')}
              limitations={t('tooltips.twoYearGrowth.limitations')}
              source={t('tooltips.twoYearGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.fiveYearGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_employment_evolution_5_year || 0
          )}
          subtitle={t('metrics.fiveYearGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_5_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.fiveYearGrowth.title')}
              shortDescription={t('tooltips.fiveYearGrowth.shortDescription')}
              calculation={t('tooltips.fiveYearGrowth.calculation')}
              importance={t('tooltips.fiveYearGrowth.importance')}
              limitations={t('tooltips.fiveYearGrowth.limitations')}
              source={t('tooltips.fiveYearGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.tenYearGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_employment_evolution_10_year || 0
          )}
          subtitle={t('metrics.tenYearGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_10_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.tenYearGrowth.title')}
              shortDescription={t('tooltips.tenYearGrowth.shortDescription')}
              calculation={t('tooltips.tenYearGrowth.calculation')}
              importance={t('tooltips.tenYearGrowth.importance')}
              limitations={t('tooltips.tenYearGrowth.limitations')}
              source={t('tooltips.tenYearGrowth.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Charts */}
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.totalEmploymentTrend.title')}
            subtitle={t('charts.totalEmploymentTrend.subtitle')}
            onDownload={() => {
              const dataUrl = totalEmploymentChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.totalEmploymentTrend.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <EmploymentTrendChart
            ref={totalEmploymentChartRef}
            data={data.trends}
            height={300}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.fullTimeVsTotal.title')}
            subtitle={t('charts.fullTimeVsTotal.subtitle')}
            onDownload={() => {
              const dataUrl = fullTimeVsTotalChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.fullTimeVsTotal.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <EmploymentTrendChart
            ref={fullTimeVsTotalChartRef}
            data={data.trends}
            height={300}
            showFullTime={true}
            t={t}
          />
        </ChartContainer>
      </ChartGrid>
    </TabContentContainer>
  )
}
